/**
 * Byte-and-count budget for localStorage caches that mint one key per place.
 *
 * WHY THIS EXISTS. localStorage is ~5–10 MB per origin and it is shared with
 * things that must never fail: the Supabase auth token and the saved-trips
 * store. Any cache that creates a new key per map cell / per route and only
 * expires a key lazily (when that exact key is read again) fills the quota
 * in about a week of ordinary driving and then EVERY other write throws.
 * We learned this twice: `poi:` on Aug 27 2026 (dead sign-in page) and
 * `ovp:v3:` on Sep 16 2026 (Sentry QuotaExceededError from Supabase's
 * token refresh, one user in Norway).
 *
 * So every per-place cache registers here and gets swept at startup and
 * again before a refused write gives up. Entries are `{ t: epochMs, v }`
 * JSON, the shape all of these caches already use.
 */

export interface PrefixBudget {
  /** localStorage key prefix, e.g. "ovp:v3:" */
  prefix: string;
  /** Entries older than this are dropped regardless of budget. */
  ttlMs: number;
  /** Hard ceiling on live entries under this prefix. */
  maxEntries: number;
  /** Hard ceiling on the summed UTF-16 length of live values (~bytes/2). */
  maxChars: number;
}

interface Entry { key: string; t: number; chars: number }

/** Every key under `prefix` with its write time and size. Unparseable → t=0 (evict first). */
function entries(prefix: string): Entry[] {
  const out: Entry[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(prefix)) continue;
    let t = 0;
    let chars = 0;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        chars = raw.length;
        t = (JSON.parse(raw) as { t?: number }).t ?? 0;
      }
    } catch { /* corrupt -- evict first */ }
    out.push({ key, t, chars });
  }
  return out;
}

/**
 * Drop expired entries, then oldest-first until both the count and the size
 * ceilings hold. `scale` shrinks both ceilings (0.5 = sweep down to half the
 * budget, used after a refused write so the retry has real headroom).
 * Returns the number of keys removed. Touches nothing outside `prefix`.
 */
export function sweepPrefix(budget: PrefixBudget, scale = 1): number {
  if (typeof window === "undefined") return 0;
  let removed = 0;
  try {
    const now = Date.now();
    const live: Entry[] = [];
    for (const e of entries(budget.prefix)) {
      if (now - e.t > budget.ttlMs) {
        window.localStorage.removeItem(e.key);
        removed++;
      } else {
        live.push(e);
      }
    }

    const maxEntries = Math.max(0, Math.floor(budget.maxEntries * scale));
    const maxChars = Math.max(0, Math.floor(budget.maxChars * scale));
    let total = live.reduce((s, e) => s + e.chars, 0);
    if (live.length > maxEntries || total > maxChars) {
      live.sort((a, b) => a.t - b.t); // oldest first
      while (live.length && (live.length > maxEntries || total > maxChars)) {
        const e = live.shift()!;
        window.localStorage.removeItem(e.key);
        total -= e.chars;
        removed++;
      }
    }
  } catch { /* best effort */ }
  return removed;
}

/**
 * setItem that makes room from ITS OWN prefix instead of failing quietly.
 * On a quota refusal: sweep the prefix down to half its budget and retry
 * once. Never throws. Returns whether the value was stored.
 */
export function setItemWithinBudget(budget: PrefixBudget, key: string, payload: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(key, payload);
    return true;
  } catch {
    // Quota (or private mode). Free our own keys, then try once more.
  }
  sweepPrefix(budget, 0.5);
  try {
    window.localStorage.setItem(key, payload);
    return true;
  } catch {
    // Still no room: something outside this cache holds the quota. The
    // cache is an optimisation, not a store of record -- drop the write.
    return false;
  }
}

// ---------------------------------------------------------------------------
// Registered budgets. Sized so the SUM stays comfortably under a 5 MB quota
// with room for the auth token, saved trips, trip history and drive logs.
// chars are UTF-16 units; localStorage's quota is usually counted the same
// way, so ~1.2M chars ≈ 2.4 MB on the pessimistic reading.
// ---------------------------------------------------------------------------

/** Overpass per-cell OSM elements (src/api/osm.ts). Up to 6000 elements per key. */
export const OVERPASS_BUDGET: PrefixBudget = {
  prefix: "ovp:v3:",
  ttlMs: 14 * 24 * 60 * 60 * 1000,
  maxEntries: 60,
  maxChars: 1_200_000,
};

/** Google Directions per-route responses (src/api/googleDirections.ts). */
export const GOOGLE_DIRECTIONS_BUDGET: PrefixBudget = {
  prefix: "gdir:v1:",
  ttlMs: 24 * 60 * 60 * 1000,
  maxEntries: 40,
  maxChars: 400_000,
};

/** Everything swept at startup, in one place so nothing is forgotten. */
export const STARTUP_BUDGETS: PrefixBudget[] = [OVERPASS_BUDGET, GOOGLE_DIRECTIONS_BUDGET];

/** Startup sweep across every registered prefix. Returns keys removed. */
export function sweepAllBudgets(): number {
  let removed = 0;
  for (const b of STARTUP_BUDGETS) removed += sweepPrefix(b);
  return removed;
}
